"use client";

export type CachedAuthUser = { name: string; email: string } | null;

const STORAGE_KEY = "bsa_auth_user";
const listeners = new Set<() => void>();

function readFromStorage(): CachedAuthUser | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return undefined;
    return raw === "null" ? null : (JSON.parse(raw) as CachedAuthUser);
  } catch {
    // Private-mode/storage-disabled browsers fall back to server-driven state only.
    return undefined;
  }
}

// Module-level, not component state: read once eagerly on the client so the
// very first render of any nav — including one mounted mid-session by a
// client-side navigation, not just page load — already has this tab's last
// known answer, no effect round trip required. undefined = nothing cached
// yet in this tab; null = confirmed logged out.
let current: CachedAuthUser | undefined =
  typeof window !== "undefined" ? readFromStorage() : undefined;

export function writeCachedAuthUser(user: CachedAuthUser): void {
  current = user;
  try {
    sessionStorage.setItem(STORAGE_KEY, user === null ? "null" : JSON.stringify(user));
  } catch {
    // Storage write failed — the in-memory value above still reaches subscribers.
  }
  for (const listener of listeners) listener();
}

export function subscribeAuthUser(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function getAuthUserSnapshot(): CachedAuthUser | undefined {
  return current;
}

export function getAuthUserServerSnapshot(): CachedAuthUser | undefined {
  return undefined;
}

// Test-only: `current` otherwise persists across tests in the same file,
// since it's module state rather than component state.
export function resetAuthCacheForTests(): void {
  current = undefined;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
